import type { FC } from "react";
import { useState } from "react";

import {
  AlertDialog,
  Card,
  CardRowDetail,
  CardRowDetailColumn,
  CardRowDetailLine,
  ColoredLabel,
  SymbolIcon,
  TimeAgo,
  TriggerButton,
  TriggerButtonIntent,
  useDialog,
} from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import styleTrack from "../styles/Track.module.scss";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { ProjectTag } from "@/modules/saved-items/components/ProjectTag.tsx";
import { TooltipController } from "@/ui-modules/core/overlays/components/OverlayControllers.tsx";
import type { InputRegionFlagUpdateInput } from "../../../graphql.js";
import { EditFlagFormDialog } from "./EditFlagForm.js";
import { FIELD_FLAG_COLOR, FIELD_FLAG_DESCRIPTION, FIELD_FLAG_NAME, FIELD_PROJECT_FLAGS, FORM_NAME } from "./_mod.js";
import type { GetRegionFlagsQuery } from "./regionFlag.gql.js";
import { useDeleteRegionFlags, useUpdateRegionFlag } from "./useFlagGraphql.js";

type Unpacked<T> = T extends Array<infer U> ? U : T;
type Flag = NonNullable<Unpacked<NonNullable<GetRegionFlagsQuery["contig"]>["flags"]>>;

interface FlagEditorProps {
  flag: Flag;
  // close the dialog is no more flag(s)
  onDelete?: () => void;
}

export const FlagEditor: FC<FlagEditorProps> = (props) => {
  const { state, underlayProps, dialogProps, dialogRef } = useDialog();

  const [isEdit, setEdit] = useState(false);
  const [deleteFlags, { loading }] = useDeleteRegionFlags();
  const [deleteError, setDeleteError] = useState<string>();
  const [updateFlag] = useUpdateRegionFlag();

  const handleEditFlag = (flag: InputRegionFlagUpdateInput) => {
    const result = updateFlag({
      flagId: props.flag.id,
      input: flag,
    });

    result.then(() => {
      setEdit(false);
    });

    // For the Form to catch errors
    return result;
  };

  const project = props.flag.project;
  const flag = props.flag;

  return (
    <Card
      className={styleTrack.flag_dialog_card}
      title={<ColoredLabel label={props.flag.name} color={props.flag.color} />}
      subtitle={
        <>
          Created by {flag?.creator?.username} <TimeAgo dateTimeISO={flag?.created} />
          {flag.modified !== flag.created ? (
            <>
              <br />
              Modified by {flag.lastModifier?.username} <TimeAgo dateTimeISO={flag.modified} />
            </>
          ) : null}
        </>
      }
      icon={
        !props.flag.shared ? (
          <TooltipController content={"This is a private flag only visible to you"}>
            {(tooltipTriggerProps) => (
              <div {...tooltipTriggerProps}>
                <SymbolIcon icon="lock" data-testid={"private"} />
              </div>
            )}
          </TooltipController>
        ) : undefined
      }
      actions={
        props.flag.deletable && !isEdit
          ? [
              <TriggerButton
                key={"update-flag-popover"}
                label={"Edit"}
                icon={"edit"}
                onPress={() => {
                  setEdit(true);
                }}
              />,
              <TriggerButton
                key={"udelete-flag-popover"}
                label={"Delete"}
                icon={"delete"}
                intent={TriggerButtonIntent.Danger}
                onPress={() => state.open()}
              />,
            ]
          : null
      }
    >
      {state.isOpen && (
        <AlertDialog
          ref={dialogRef}
          title={"Flag Deletion"}
          mainButton={{
            label: "Delete",
            isLoading: loading,
            onPress: () =>
              deleteFlags({ regionFlagIds: [props.flag.id] })
                .then(() => {
                  // TODO: Update table?
                  // TODO: Show a notification
                  props.onDelete?.();
                  state.close();
                })
                .catch((error) => setDeleteError(error.message)),
            icon: "delete",
          }}
          errorCallout={deleteError ? { message: deleteError } : undefined}
          domProps={dialogProps}
          closeFn={state.close}
          underlayProps={underlayProps}
        >
          Do you really want to delete the "{props.flag.name}" flag?
        </AlertDialog>
      )}
      {isEdit ? (
        <EditFlagFormDialog
          form={`${FORM_NAME}_${flag.id}`}
          projectId={props.flag.project.id}
          initialValues={{
            [FIELD_FLAG_NAME]: props.flag.name,
            [FIELD_FLAG_DESCRIPTION]: props.flag.description,
            [FIELD_FLAG_COLOR]: props.flag.color,
            [FIELD_PROJECT_FLAGS]: props.flag.itemTags.map((tag) => tag.id),
          }}
          onCancel={() => {
            setEdit(false);
          }}
          onSaveFlagEditing={handleEditFlag}
        />
      ) : (
        <CardRowDetail>
          <CardRowDetailColumn>
            <CardRowDetailLine title="Location">
              {flag.region?.contig?.sequenceId}: {formatNumberByThousands(flag.region?.range?.start)}-
              {formatNumberByThousands(flag.region?.range?.stop)}
            </CardRowDetailLine>
            {project && (
              <CardRowDetailLine title="Project">
                <ProjectColoredLink project={project} />
              </CardRowDetailLine>
            )}
            {props.flag.description && (
              <CardRowDetailLine title="Description">{props.flag.description}</CardRowDetailLine>
            )}
            {props.flag?.itemTags.length > 0 && (
              <CardRowDetailLine title="Tags">
                {props.flag?.itemTags.map((tag) => <ProjectTag key={tag.id} projectTag={tag} />)}
              </CardRowDetailLine>
            )}
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
    </Card>
  );
};
